import { precise_round } from '@gameday/common/functions'
import { uuid } from '@gameday/common/utils'
import { isEmpty, isFunction } from 'lodash'
import { put, select, takeLatest } from 'redux-saga/effects'

import {
  braintree,
  common,
  error,
  formData as formActions,
  formInfo,
  payment as paymentActions,
} from '../constants/actions'
import { formData as endpointFormData, formSettings } from '../constants/endpoints'
import {
  getFormId,
  getFormInfo,
  getFormType,
  getOrganisationId,
  getSelectedPaymentGateway,
  getSessionId,
  getUserId,
} from '../selectors'
import { get, post } from '../utils/request'

function* validateSessions({ payload }) {
  yield put(
    error.onSummaryPageError({
      title: null,
    })
  )
  yield put(common.setShowLoadingIndicator(true))

  const { sessions, paymentDetails, next } = payload
  const formType = yield select(getFormType)
  const fInfo = yield select(getFormInfo)
  const type = fInfo?.form?.type || formType

  const sessionId = yield select(getSessionId)

  const response =
    (type === 'event' || sessionId) && !isEmpty(sessions.products)
      ? yield post({
          endpointId: endpointFormData.validateSessions,
          data: {
            ...sessions,
            session: sessionId,
          },
        })
      : { success: true }

  if (response.success) {
    if (sessions.source === 'Summary') {
      if (type === 'event' || sessionId) {
        yield put(formActions.setSessions(sessions))
      }

      yield put(formActions.submitRegistrations(payload))
    } else if (sessions.source === 'Payment') {
      yield put(paymentActions.submitPayment(paymentDetails, next))
    }
  } else {
    if (sessions.source === 'Summary') {
      yield put(
        error.onSummaryPageError({
          title: response.errorMessage || response.error || 'An error occurred',
          showInterval: 10000,
        })
      )
    } else if (sessions.source === 'Payment') {
      yield put(
        error.onPaymentPageError({
          title: response.errorMessage || response.error || 'An error occurred',
        })
      )
    }
    yield put(common.setShowLoadingIndicator(false))
  }
}

function* submitRegistrations({ payload }) {
  const gateway = yield select(getSelectedPaymentGateway)
  yield put(common.setShowLoadingIndicator(true))

  const { registrations, paymentData, next, team, isTeamRego, insuranceQuote } = payload
  const { teamDetails, ...restTeam } = team || {}
  const paymentGateway = gateway.parentId || gateway.id

  const response = yield post({
    endpointId: endpointFormData.submitRegistrations,
    data: {
      ...registrations,
      ...(isTeamRego && {
        ...restTeam,
      }),
      amount: Number(precise_round(isTeamRego ? team.amount : registrations.amount, 2)),
      paymentGateway,
      team,
      ...(gateway.name === 'Stripe' && {
        isInstalmentsPayment: !!gateway.isInstalmentsPayment,
      }),
      ...(insuranceQuote && {
        insurance: insuranceQuote,
      }),
    },
  })

  if (response.success) {
    yield put(paymentActions.setPaymentData(paymentData))
    yield put(formActions.setOrderId(response.data.orderId))
    yield put(formActions.setTeamCode(response.data.teamCode))
    yield put(formActions.setRegistrationData(isTeamRego ? team : registrations))
    yield put(formActions.setInitialPaymentGateway(paymentGateway))
    if (paymentData.total.rawValue === 0) {
      yield put(paymentActions.paymentSuccessful(true))
      yield put(common.setStartTimer(false))
      yield put(common.setShowLoadingIndicator(false))
      yield put(paymentActions.submitPostPayment({ amount: 0, teamDetails, teamCode: response?.data?.teamCode }, next))
    } else {
      next()
    }
  } else {
    yield put(
      error.onSummaryPageError({
        title: response.errorMessage,
      })
    )
    yield put(common.setShowLoadingIndicator(false))
  }
}

function* createAndValidateSession(action) {
  const sessionId = uuid()
  yield put(formActions.setSessionId(sessionId))
  const { source, products } = action.payload
  const session = {
    source,
    products,
  }
  yield validateSession({
    payload: session,
    callback: action.callback,
  })
}

function* validateSession(action) {
  yield put(common.setShowLoadingIndicator(true))
  const session = {
    ...action.payload,
    loggedInUser: yield select(getUserId),
    form: yield select(getFormId),
    session: yield select(getSessionId),
  }

  const response = yield post({
    endpointId: endpointFormData.validateSessions,
    data: session,
  })

  yield put(common.setShowLoadingIndicator(false))
  if (isFunction(action.callback)) {
    action.callback({
      success: response.success,
      message: response.errorMessage,
    })
  }
}

function* resetForm(action) {
  yield put(common.setShowLoadingIndicator(true))

  // get user information from token
  const userid = yield select(getUserId)
  const isSignout = action.payload
  const element = document.getElementById('Cardinal-ElementContainer')

  if (element) {
    element.remove()
  }

  if (!isSignout && userid) {
    // fetch form details
    const formDetailResponse = yield get({
      endpointId: formSettings.getFormDetails,
      params: {
        form: yield select(getFormId),
        organisation: yield select(getOrganisationId),
        user: userid,
      },
    })

    // set form data
    if (formDetailResponse.data) {
      yield put(formInfo.setFormInfo(formDetailResponse.data))
    }
  }

  // clear Teak insurance widget
  if (typeof tg?.clear === 'function') {
    tg.clear()
    tg.removePersistedSession()
  }

  yield put(paymentActions.setBraintreePaymentToken(null))
  yield put(braintree.setBTPaymentMethods([]))
  yield put(common.setShowLoadingIndicator(false))
}

export function* runFormDataSagas() {
  yield takeLatest(formActions.VALIDATE_SESSIONS, validateSessions)
  yield takeLatest(formActions.SUBMIT_REGISTRATIONS, submitRegistrations)
  yield takeLatest(formActions.CREATE_VALIDATE_SESSION, createAndValidateSession)
  yield takeLatest(formActions.VALIDATE_SESSION, validateSession)
  yield takeLatest(formActions.RESET_FORM, resetForm)
}
